import React, { useMemo, useState } from 'react';
import '../App.css';

import IMG_MAIN from '../assets/1.png';
import IMG_CARD1 from '../assets/card_1.png';

import ICON_YOUTUBE from '../assets/y.png';
import ICON_FACEBOOK from '../assets/f.png';
import ICON_INSTA from '../assets/i.png';

import { useNavigate } from "react-router-dom";
import TourCard from '../components/TourCard';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import { useTour } from "../context/TourContext";
import AllReviews from '../components/AllReviews';
import TourGallery from '../components/TourGallery';
import TourFilters from '../components/TourFilters';

function HomePage() {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { tours, loading, error, retryTours, getParticipants, selectedDate } = useTour();
  const [tourQuery, setTourQuery] = useState("");
  const [tourCategory, setTourCategory] = useState("all");
  const [availableOnly, setAvailableOnly] = useState(false);

  const categories = useMemo(() => Array.from(new Set(
    tours.flatMap((tour) => Array.isArray(tour.tags) ? tour.tags : [])
  )).sort(), [tours]);

  const filteredTours = useMemo(() => {
    const normalizedQuery = tourQuery.trim().toLowerCase();
    return tours.filter((tour) => {
      const tags = Array.isArray(tour.tags) ? tour.tags : [];
      const searchableText = [tour.title, tour.description, ...tags].filter(Boolean).join(" ").toLowerCase();
      const matchesQuery = !normalizedQuery || searchableText.includes(normalizedQuery);
      const matchesCategory = tourCategory === "all" || tags.includes(tourCategory);
      const matchesAvailability = !availableOnly || Number(tour.participant_count ?? 0) < 45;
      return matchesQuery && matchesCategory && matchesAvailability;
    });
  }, [availableOnly, tourCategory, tourQuery, tours]);

  const resetFilters = () => {
    setTourQuery("");
    setTourCategory("all");
    setAvailableOnly(false);
  };

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden font-poppins">
      <Header />

      {/* Hero */}
      <div className="relative">
        <img
          src={IMG_MAIN}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/0 to-black/55 pointer-events-none" />
        <div className="relative z-10
                        px-[80px] py-[60px]
                        lg:px-[24px] lg:py-[40px]
                        md:px-[24px] md:py-[40px]
                        font-poppins">
          <div className="text-center text-[64px] lg:text-[38px] md:text-[38px] font-bold leading-[1.1] text-white">ODK</div>
          <div className="absolute left-[80px] top-1/2 -translate-y-1/2 flex flex-col text-white
                          lg:static lg:translate-y-0 lg:mb-8
                          md:static md:translate-y-0 md:mb-8">
          </div>

          <div className="mx-auto max-w-[900px]">
            <AllReviews />
          </div>
        </div>
      </div>

      <TourGallery />

      {/* Tour Cards */}
      <section className="relative bg-gray-100 pb-20 pt-0 md:pb-10 md:pt-6">
        <div className="absolute inset-x-0 top-0 h-[230px] bg-white md:h-[360px]" aria-hidden="true" />
        <div className="relative flex justify-center md:px-4">
          <div className="relative z-10 w-full max-w-[900px]">
            {!loading && !error && tours.length > 0 && (
              <div className="mb-6 md:mb-5">
                <TourFilters
                  categories={categories}
                  category={tourCategory}
                  onCategoryChange={setTourCategory}
                  query={tourQuery}
                  onQueryChange={setTourQuery}
                  availableOnly={availableOnly}
                  onAvailableOnlyChange={setAvailableOnly}
                  resultCount={filteredTours.length}
                  onReset={resetFilters}
                />
              </div>
            )}
            <div className="flex flex-col gap-[36px] md:gap-[20px]">
              {loading ? (
                <div className="flex flex-col gap-[36px] md:gap-[20px]" aria-label="Loading tours">
                  {[0, 1].map((item) => (
                    <div key={item} className="grid aspect-[3.55/1] grid-cols-2 overflow-hidden rounded-[36px] bg-white shadow-xl md:aspect-auto md:grid-cols-1">
                      <div className="animate-pulse bg-gray-200 md:aspect-video" />
                      <div className="space-y-4 p-10 md:p-6">
                        <div className="h-7 w-2/3 animate-pulse rounded bg-gray-200" />
                        <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                        <div className="h-3 w-4/5 animate-pulse rounded bg-gray-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="rounded-[28px] bg-white px-6 py-14 text-center shadow-xl" role="alert">
                  <div className="mb-3 text-[32px]" aria-hidden="true">!</div>
                  <h2 className="m-0 text-[18px] font-semibold text-gray-800">Unable to load tours</h2>
                  <p className="mx-auto mb-6 mt-2 max-w-[480px] text-[13px] leading-relaxed text-gray-400">{error}</p>
                  <button type="button" onClick={retryTours} className="rounded-full border-0 bg-[#2C2C2A] px-6 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-black">
                    Try again
                  </button>
                </div>
              ) : tours.length === 0 ? (
                <div className="rounded-[28px] bg-white px-6 py-14 text-center shadow-xl">
                  <h2 className="m-0 text-[18px] font-semibold text-gray-800">No tours available</h2>
                  <p className="mb-0 mt-2 text-[13px] text-gray-400">Try selecting another date.</p>
                </div>
              ) : filteredTours.length === 0 ? (
                <div className="rounded-[28px] bg-white px-6 py-14 text-center shadow-xl">
                  <h2 className="m-0 text-[18px] font-semibold text-gray-800">No matching tours</h2>
                  <p className="mb-5 mt-2 text-[13px] text-gray-400">Try changing or resetting your filters.</p>
                  <button type="button" onClick={resetFilters} className="rounded-full border border-gray-200 bg-white px-5 py-2 text-[12px] font-semibold text-gray-600 hover:border-[#E8876A] hover:text-[#E8876A]">Reset filters</button>
                </div>
              ) : (
                filteredTours.map((tour) => (
                  <TourCard
                    key={tour.id}
                    tourId={tour.id}
                    images={tour.images}
                    title={tour.title}
                    tags={tour.tags}
                    description={tour.description}
                    participantCount={getParticipants(tour.id)}
                    imgSrc={IMG_CARD1}
                    width={900}
                    selectedDate={selectedDate}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="flex flex-col bg-[#2C2C2A] text-white px-[60px] py-[30px] gap-4 text-[12px]
                   md:px-[24px] md:py-[24px]">

        {/* 사업자 정보 */}
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[#ffffff80]">
          <span>상호 : (주)코리아트래블스토어2</span>
          <span>대표 : 유영재</span>
          <span>사업자등록번호 : 142-81-87178</span>
          <span>통신판매신고번호 : 제 2026-서울영등포-1470호</span>
          <span>주소 : 서울특별시 영등포구 국회대로70길 15-1, 1111호</span>
          <span>전화 : 010-5304-4220</span>
          <span>이메일 : help@onedaykorea.co.kr</span>
        </div>

        {/* 이미지 출처 */}
        <div className="text-[#ffffff50] text-[11px]">
          일부 이미지 출처 : 문화재청 국가유산포털 (www.heritage.go.kr), 공공누리 제1유형
        </div>

        {/* 하단 */}
        <div className="flex flex-row justify-between items-center md:flex-col md:items-start md:gap-3">
          <div className="flex gap-4">
            <span
              className="text-[#ffffff80] hover:text-white cursor-pointer transition-colors"
              onClick={() => navigate("/terms")}
            >
              Terms of Service
            </span>
            <span
              className="text-[#ffffff80] hover:text-white cursor-pointer transition-colors"
              onClick={() => navigate("/privacy")}
            >
              Privacy Policy
            </span>
          </div>

          {/* <div className="flex flex-row gap-7 items-center">
            <img src={ICON_YOUTUBE} className="w-[25px] h-[17px]" />
            <img src={ICON_FACEBOOK} className="w-[12px] h-[20px]" />
            <img src={ICON_INSTA} className="w-[20px] h-[20px]" />
          </div> */}

          <div className="text-[#ffffff80]">
            COPYRIGHT © 2026. (주)코리아트래블스토어2. ALL RIGHTS RESERVED.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
